/**
 * Resolve a single product's rate config from window.ghpRateService.
 *
 * PHP emits window.ghpRateService as a map keyed by product ID so multiple
 * See-my-Price blocks can coexist. When a productId is given, return that
 * entry. Otherwise return the sole entry (single-product pages).
 *
 * @param {number|null} productId
 * @returns {object|null}
 */
export function resolveRateConfig(productId = null) {
  const root = window.ghpRateService;
  if (!root || typeof root !== "object") {
    return null;
  }

  if (productId != null) {
    // A specific product was requested — only its own entry is correct.
    // Never fall back to a different product's config.
    return root[productId] || null;
  }
  // No product requested (single-product page): return the only entry.
  const keys = Object.keys(root);
  if (keys.length === 1) {
    return root[keys[0]];
  }
  return null;
}

/**
 * Client-side GHP Rate Service.
 *
 * Mirrors the PHP GHP_Rate_Engine class. Expects rate data
 * to be available on window.ghpRateService (set by PHP via
 * wp_add_inline_script when a GHP page is rendered).
 *
 * @param {object|number|null} configOrProductId A resolved config object, or a
 *   product ID to look up in window.ghpRateService, or null for the sole entry.
 */
export default class GhpRateService {
  constructor(configOrProductId = null) {
    const config =
      configOrProductId && typeof configOrProductId === "object"
        ? configOrProductId
        : resolveRateConfig(configOrProductId);

    if (!config) {
      this.valid = false;
      return;
    }

    this.valid = true;
    this.productId = config.productId;
    this.productName = config.productName;
    this.ageBands = config.ageBands;
    this.excludedStates = config.excludedStates;
    this.coverageAmount = config.coverageAmount;
    this.maxChildren = config.maxChildren;
  }

  getAgeBands() {
    if (!this.valid) {
      return { error: true, code: 'service_unavailable', message: 'GHP rate data is not loaded.' };
    }

    return this.ageBands;
  }

  getRate(age) {
    if (!this.valid) {
      return { error: true, code: 'service_unavailable', message: 'GHP rate data is not loaded.' };
    }

    const numAge = Number(age);

    if (Number.isNaN(numAge)) {
      return { error: true, code: 'invalid_age', message: 'A valid age is required.' };
    }

    for (const band of this.ageBands) {
      if (numAge >= band.ageFrom && numAge <= band.ageTo) {
        return {
          rate: band.rate,
          ageBand: `${band.ageFrom}-${band.ageTo}`,
        };
      }
    }

    return { error: true, code: 'age_not_eligible', message: `Age ${numAge} is not eligible for this product.` };
  }

  isStateExcluded(state) {
    if (!this.valid) {
      return true;
    }

    return this.excludedStates.includes(String(state ?? '').toUpperCase().trim());
  }

  getExcludedStates() {
    if (!this.valid) {
      return { error: true, code: 'service_unavailable', message: 'GHP rate data is not loaded.' };
    }

    return this.excludedStates;
  }

  validateAndGetRate(age, state) {
    if (!this.valid) {
      return { error: true, code: 'service_unavailable', message: 'GHP rate data is not loaded.' };
    }

    const normalizedState = String(state ?? '').toUpperCase().trim();

    if (this.excludedStates.includes(normalizedState)) {
      return { error: true, code: 'state_not_eligible', message: `This product is not available in ${normalizedState}.` };
    }

    return this.getRate(age);
  }

  calculateTotal(persons) {
    if (!this.valid) {
      return { error: true, code: 'service_unavailable', message: 'GHP rate data is not loaded.' };
    }

    const results = [];
    const errors = [];
    let total = 0;

    persons.forEach((person, index) => {
      const result = this.validateAndGetRate(person.age, person.state);

      if (result.error) {
        errors.push({ index, code: result.code, message: result.message });
        return;
      }

      results.push({
        age: person.age,
        state: String(person.state ?? '').toUpperCase().trim(),
        rate: result.rate,
        ageBand: result.ageBand,
      });
      total += result.rate;
    });

    if (errors.length > 0) {
      return {
        error: true,
        code: 'eligibility_errors',
        message: 'One or more persons are not eligible.',
        errors,
      };
    }

    return {
      persons: results,
      total: Math.round(total * 100) / 100,
    };
  }
}
